import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { guestService } from "@/lib/api";
import type { GuestUpdate } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  language: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface GuestEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Guest to edit; the dialog fetches the full record (for notes). */
  guestId: string | null;
  isPending?: boolean;
  onSubmit: (values: GuestUpdate) => void;
}

export function GuestEditDialog({
  open,
  onOpenChange,
  guestId,
  isPending,
  onSubmit,
}: GuestEditDialogProps) {
  const { data: guest } = useQuery({
    queryKey: ["guest", guestId],
    queryFn: () => guestService.get(guestId as string),
    enabled: open && Boolean(guestId),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Prefill once the guest record loads (and re-prefill per open).
  useEffect(() => {
    if (open) {
      reset({
        language: guest?.language ?? "",
        email: guest?.email ?? "",
        phone: guest?.phone ?? "",
        notes: guest?.notes ?? "",
      });
    }
  }, [open, guest, reset]);

  const submit = handleSubmit((values) => {
    // Empty strings clear nothing server-side; send only trimmed values.
    const payload: GuestUpdate = {};
    if (values.language?.trim()) payload.language = values.language.trim();
    if (values.email?.trim()) payload.email = values.email.trim();
    if (values.phone?.trim()) payload.phone = values.phone.trim();
    if (values.notes?.trim()) payload.notes = values.notes.trim();
    onSubmit(payload);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>게스트 편집</DialogTitle>
          <DialogDescription>게스트의 연락처와 메모를 수정합니다.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="guest-edit-language">언어</Label>
              <Input id="guest-edit-language" {...register("language")} placeholder="ko" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guest-edit-phone">전화</Label>
              <Input id="guest-edit-phone" {...register("phone")} placeholder="010-0000-0000" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guest-edit-email">이메일</Label>
            <Input id="guest-edit-email" type="email" {...register("email")} />
            {errors.email ? (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="guest-edit-notes">메모</Label>
            <Textarea id="guest-edit-notes" {...register("notes")} placeholder="특이사항" />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              취소
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              저장
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
